// @ts-nocheck
// Player action buttons for the Team page's Registration Details card:
//  - "Create New Player": the existing full add-player form (no email).
//  - "Invite Player": name + email → creates a stub player AND emails them the
//    player_invitation (same email the apps/app players-submission flow sends).
// Extracted from the old TeamPlayersTable so the standalone Players grid can go.
import React, { useState } from 'react';
import { AddForm, extractFormData } from '@components/DataTable/AddForm';
import MuiDialog from '@mui/material/Dialog';
import { ColumnInterface } from '@/interfaces';
import { useAuth } from '@/auth/clerkShim';
import helpers from '@/utils/helpers';
import usePlayerStore from '@/store/playerStore';
import { buildPlayerFieldColumns, extractPlayerInfoFromForm, buildPlayerPayload } from '@/utils/playerFields';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Mail, UserPlus, Download } from 'lucide-react';
import { toast } from 'sonner';
import { downloadReleaseStatement, hasPrintableReleaseContent } from '@/utils/printReleaseStatement';

interface TeamPlayerActionsProps {
  team: any;
  registration: any;
  organization: any;
  leagueId?: string;
  tournamentId?: string;
  contestType?: string;
  divisionId?: string;
  onChanged?: () => void;
}

export default function TeamPlayerActions({
  team,
  registration,
  organization,
  leagueId,
  tournamentId,
  contestType,
  divisionId,
  onChanged,
}: TeamPlayerActionsProps) {
  const { getToken } = useAuth();
  const teamId = team?._id;

  const addPlayer = usePlayerStore((state: any) => state.addPlayer(getToken));
  const invitePlayer = usePlayerStore((state: any) => state.invitePlayer(getToken));

  const [addOpen, setAddOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ first_name: '', last_name: '', email: '' });
  const [inviting, setInviting] = useState(false);

  // Invite creates a stub player AND emails via the registrationTeam — the
  // endpoint hard-requires one, so hide the button for manually-added teams.
  const canInvite = Boolean(team?.registrationTeam);

  // Shared payload of team/registration refs every player on this team needs.
  const playerRefs = () => {
    const refs: any = { team: teamId, league: leagueId, organization: organization?._id };
    if (contestType === 'tournament' && tournamentId) refs.tournament = tournamentId;
    if (divisionId) refs.division = divisionId;
    if (registration?._id) refs.registration = registration._id;
    if (team?.registrationTeam) refs.registrationTeam = helpers.getObjId(team.registrationTeam);
    return refs;
  };

  // ── Create New Player (full form, no email) ──────────────────────────────────
  const onAdd = async (data) => {
    const info = extractPlayerInfoFromForm(data);
    const player = buildPlayerPayload(info, playerRefs());
    const res = await addPlayer(player);
    onChanged?.();
    return res;
  };

  // First/Last + registration.fields → columns (shared with the division Add Team modal).
  const columns: ColumnInterface[] = buildPlayerFieldColumns(registration);
  const formSchema = extractFormData(columns);

  // ── Invite Player (name + email → stub player + player_invitation email) ──────
  const submitInvite = async () => {
    const email = inviteForm.email.trim();
    if (!email) { toast.error('Email is required'); return; }
    setInviting(true);
    try {
      const res = await invitePlayer({
        ...playerRefs(),
        info: { first_name: inviteForm.first_name.trim(), last_name: inviteForm.last_name.trim(), email },
      });
      if (!res?.error) {
        setInviteOpen(false);
        setInviteForm({ first_name: '', last_name: '', email: '' });
        onChanged?.();
      }
    } finally {
      setInviting(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Button onClick={() => setAddOpen(true)}>
        <span className="inline-flex items-center gap-1"><UserPlus className="h-4 w-4" />Create New Player</span>
      </Button>

      {/* Snappy MUI Dialog (matches CreatePaymentModal) instead of the laggy
          framer-motion ShowModalButton modal. AddForm closes it via onClose. */}
      <MuiDialog open={addOpen} onClose={() => setAddOpen(false)}>
        <AddForm onSave={onAdd} onClose={() => setAddOpen(false)} dataList={[]} formSchema={formSchema} title={'Add Player'} />
      </MuiDialog>

      {canInvite && (
        <Button variant="outline" onClick={() => setInviteOpen(true)}>
          <Mail className="h-4 w-4 mr-1" />
          Invite Player
        </Button>
      )}

      {hasPrintableReleaseContent(registration) && (
        <Button variant="outline" onClick={() => downloadReleaseStatement(registration)}>
          <Download className="h-4 w-4 mr-1" />
          Download Release Form
        </Button>
      )}

      <Dialog open={inviteOpen && canInvite} onOpenChange={(o) => { if (!o) setInviteOpen(false); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Player</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Adds the player to this team and emails them an invitation to complete their registration.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="invite-first">First Name</Label>
                <Input id="invite-first" value={inviteForm.first_name} onChange={(e) => setInviteForm((f) => ({ ...f, first_name: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="invite-last">Last Name</Label>
                <Input id="invite-last" value={inviteForm.last_name} onChange={(e) => setInviteForm((f) => ({ ...f, last_name: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="invite-email">Email</Label>
              <Input id="invite-email" type="email" value={inviteForm.email} onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteOpen(false)} disabled={inviting}>Cancel</Button>
            <Button onClick={submitInvite} disabled={inviting || !inviteForm.email.trim()}>
              {inviting ? 'Sending…' : 'Send Invite'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
